import { useQuery, gql, useApolloClient } from '@apollo/client';
import { useMeContext } from '~/modules/me/useMeContext';

export const TASK_ASSIGNMENT_COUNTS_QUERY = gql`
  query taskAssignmentCounts(
    $projectId: String!
    $page: Int
    $pagesize: Int
    $filter: GetResourceUsersFilter
    $textSearch: String
  ) {
    project(projectId: $projectId) {
      id
      tasksCount(isOpen: true, textSearch: $textSearch)
    }
    resourceUsers2(page: $page, pagesize: $pagesize, filter: $filter) {
      id
      assignedTasksCount(
        assignedTasksCountInput: {
          projectId: $projectId
          textSearch: $textSearch
        }
      )
    }
  }
`;

export const PROJECT_TASK_ASSIGNMENT_COUNT_FOR_USER_QUERY = gql`
  query projectTaskAssignmentCountForUser(
    $projectId: String!
    $userId: String!
    $textSearch: String
  ) {
    project(projectId: $projectId) {
      id
      tasksCount(isOpen: true, textSearch: $textSearch)
      assignedTasksCountForUser(
        assignedTasksCountInput: { userId: $userId, textSearch: $textSearch }
      )
    }
  }
`;

const updateTaskAssignmentCountsCache = ({
  proxy,
  query,
  variables,
  useProjectTaskAssignmentCountForUserQuery
}) => isAssignedTask => {
  const data = proxy.readQuery({ query, variables });

  const delta = isAssignedTask ? -1 : 1;
  const previousAssignedTasksCount = useProjectTaskAssignmentCountForUserQuery
    ? data.project.assignedTasksCountForUser
    : data.resourceUsers2[0].assignedTasksCount;

  const updatedData = useProjectTaskAssignmentCountForUserQuery
    ? {
        ...data,
        project: {
          ...data.project,
          assignedTasksCountForUser: previousAssignedTasksCount + delta
        }
      }
    : {
        ...data,
        resourceUsers2: [
          {
            ...data.resourceUsers2[0],
            assignedTasksCount: previousAssignedTasksCount + delta
          }
        ]
      };

  proxy.writeQuery({ query, data: updatedData, variables });
};

export default function useTaskAssignmentCounts({
  projectId,
  userId,
  textSearch
}) {
  const apolloClient = useApolloClient();
  const {
    featureFlags: { isPsaRmpEnhancedProjectManagerPersonaEnabled }
  } = useMeContext();

  const useProjectTaskAssignmentCountForUserQuery = isPsaRmpEnhancedProjectManagerPersonaEnabled;

  const query = useProjectTaskAssignmentCountForUserQuery
    ? PROJECT_TASK_ASSIGNMENT_COUNT_FOR_USER_QUERY
    : TASK_ASSIGNMENT_COUNTS_QUERY;

  const variables = useProjectTaskAssignmentCountForUserQuery
    ? { projectId, userId, textSearch }
    : {
        projectId,
        page: 1,
        pagesize: 1,
        filter: { userIds: [userId] },
        textSearch
      };

  const { loading, data } = useQuery(query, {
    variables,
    skip: !projectId || !userId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first'
  });

  const assignedTasksCount = useProjectTaskAssignmentCountForUserQuery
    ? data?.project?.assignedTasksCountForUser
    : data?.resourceUsers2?.[0]?.assignedTasksCount;

  return {
    loading,
    totalTasksCount: data?.project?.tasksCount,
    assignedTasksCount,
    updateTaskAssignmentCountsCache: updateTaskAssignmentCountsCache({
      proxy: apolloClient.cache,
      query,
      variables,
      useProjectTaskAssignmentCountForUserQuery
    })
  };
}
